import { Address } from '../../types/address';
import {
  Business,
  BusinessInvitation,
  BusinessMembership,
} from '../../types/business';

export type MockBuyerAccount = {
  email: string;
  password: string;
  userId: string;
  fullName: string;
  phone?: string;
  defaultShippingAddress?: Address;
  defaultBillingAddress?: Address;
  /** When 'PENDING_REVIEW', login still succeeds but identity flow lands on /account-inactive. */
  status: 'ACTIVE' | 'DISABLED' | 'PENDING_REVIEW';
};

const sydneyAddress: Address = {
  line1: 'Level 2, 88 King Street',
  suburb: 'Sydney',
  state: 'NSW',
  postcode: '2000',
  country: 'AU',
};

const parraAddress: Address = {
  line1: 'Suite 4, 12 Macquarie Street',
  suburb: 'Parramatta',
  state: 'NSW',
  postcode: '2150',
  country: 'AU',
};

export const MOCK_BUYER_ACCOUNTS: MockBuyerAccount[] = [
  // Owner of an ACTIVE Business (XYZ Medical Centre)
  {
    email: 'active@clinic.test',
    password: 'password',
    userId: 'u-sarah',
    fullName: 'Sarah Chen',
    phone: '+61 412 555 089',
    status: 'ACTIVE',
  },
  // Member of the same ACTIVE Business
  {
    email: 'member@buyer.test',
    password: 'password',
    userId: 'u-mira',
    fullName: 'Mira Patel',
    phone: '+61 412 555 102',
    status: 'ACTIVE',
  },
  // Individual buyer with default addresses
  {
    email: 'individual@buyer.test',
    password: 'password',
    userId: 'u-lin',
    fullName: 'Lin Zhao',
    phone: '+61 412 555 200',
    defaultShippingAddress: { ...parraAddress },
    defaultBillingAddress: { ...parraAddress },
    status: 'ACTIVE',
  },
  // Owner of a DISABLED Business (lands on /account-inactive when trying buyer flows)
  {
    email: 'disabled@clinic.test',
    password: 'password',
    userId: 'u-dan',
    fullName: 'Dan Smith',
    status: 'ACTIVE',
  },
  // Pending-review user (for testing /account-inactive when user.status != ACTIVE)
  {
    email: 'pending@buyer.test',
    password: 'password',
    userId: 'u-pending',
    fullName: 'Penny Pending',
    status: 'PENDING_REVIEW',
  },
];

export const MOCK_BUSINESSES: Business[] = [
  {
    id: 'biz-xyz',
    name: 'XYZ Medical Centre',
    abn: '12 345 678 901',
    industry: 'Traditional Chinese Medicine Clinic',
    businessPhone: '+61 2 9000 0000',
    businessEmail: 'admin@xyzmedical.test',
    contactPerson: 'Sarah Chen',
    billingAddress: { ...sydneyAddress },
    shippingAddress: { ...sydneyAddress },
    status: 'ACTIVE',
    ownerUserId: 'u-sarah',
  },
  {
    id: 'biz-oldtown',
    name: 'Old Town Clinic',
    industry: 'GP Clinic',
    businessPhone: '+61 2 9111 1111',
    businessEmail: 'admin@oldtown.test',
    billingAddress: { ...parraAddress },
    shippingAddress: { ...parraAddress },
    status: 'DISABLED',
    ownerUserId: 'u-dan',
  },
];

/**
 * Source of truth for buyer identity (V2.2). At most one ACTIVE row per userId
 * (matches the DB unique-index constraint described in docs/business-rules.md).
 */
export const MOCK_MEMBERSHIPS: BusinessMembership[] = [
  {
    id: 'mem-sarah-xyz',
    businessId: 'biz-xyz',
    userId: 'u-sarah',
    role: 'OWNER',
    status: 'ACTIVE',
    joinedAt: '2024-03-12T00:00:00Z',
  },
  {
    id: 'mem-mira-xyz',
    businessId: 'biz-xyz',
    userId: 'u-mira',
    role: 'MEMBER',
    status: 'ACTIVE',
    joinedAt: '2024-04-01T00:00:00Z',
    invitedByUserId: 'u-sarah',
  },
  {
    id: 'mem-dan-oldtown',
    businessId: 'biz-oldtown',
    userId: 'u-dan',
    role: 'OWNER',
    status: 'ACTIVE',
    joinedAt: '2023-11-02T00:00:00Z',
  },
];

const sevenDaysFromNow = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString();
};

export const MOCK_INVITATIONS: BusinessInvitation[] = [
  {
    id: 'inv-james',
    businessId: 'biz-xyz',
    invitedEmail: 'james.wong@example.test',
    invitedByUserId: 'u-sarah',
    token: 'inv-token-james',
    expiresAt: sevenDaysFromNow(),
    status: 'PENDING',
    createdAt: new Date().toISOString(),
  },
];
